// stereo-core: axial directional statistics (Fisher 1953; Woodcock 1977).
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, DEG, dot, unit } from "./vec.js";

// [STA-01]
/** Eigen-decomposition of a symmetric 3x3 matrix (Jacobi). Eigenvalues descending. */
export function eigSym3(m: number[][]): { values: number[]; vectors: Vec3[] } {
  const a = m.map((r) => r.slice());
  const v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let sweep = 0; sweep < 50; sweep++) {
    let off = 0;
    for (let i = 0; i < 3; i++) for (let j = i + 1; j < 3; j++) off += a[i][j] ** 2;
    if (off < 1e-22) break;
    for (let p = 0; p < 3; p++) for (let q = p + 1; q < 3; q++) {
      if (Math.abs(a[p][q]) < 1e-300) continue;
      const th = (a[q][q] - a[p][p]) / (2 * a[p][q]);
      const t = Math.sign(th || 1) / (Math.abs(th) + Math.sqrt(th * th + 1));
      const c = 1 / Math.sqrt(t * t + 1), s = t * c;
      for (let k = 0; k < 3; k++) {
        const akp = a[k][p], akq = a[k][q];
        a[k][p] = c * akp - s * akq; a[k][q] = s * akp + c * akq;
      }
      for (let k = 0; k < 3; k++) {
        const apk = a[p][k], aqk = a[q][k];
        a[p][k] = c * apk - s * aqk; a[q][k] = s * apk + c * aqk;
      }
      for (let k = 0; k < 3; k++) {
        const vkp = v[k][p], vkq = v[k][q];
        v[k][p] = c * vkp - s * vkq; v[k][q] = s * vkp + c * vkq;
      }
    }
  }
  const idx = [0, 1, 2].sort((i, j) => a[j][j] - a[i][i]);
  return {
    values: idx.map((i) => a[i][i]),
    vectors: idx.map((i) => [v[0][i], v[1][i], v[2][i]] as Vec3),
  };
}

// [STA-01]
/** Normalised orientation tensor. */
export function orientationTensor(vs: Vec3[], w?: number[]): number[][] {
  const t = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
  let W = 0;
  vs.forEach((v0, k) => {
    const v = unit(v0), wk = w ? w[k] : 1;
    W += wk;
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) t[i][j] += wk * v[i] * v[j];
  });
  return t.map((r) => r.map((x) => x / W));
}

export interface FisherStats {
  n: number;            // number of poles (raw count)
  weightSum: number;    // sum of weights (= n when unweighted)
  mean: Vec3;           // mean pole, lower hemisphere
  R: number;            // resultant length (weighted)
  K: number | null;     // Fisher K estimate (N-1)/(N-R); null if n < 2
  confidenceDeg: number | null; // cone of confidence at `level`
  level: number;
  variability1Deg: number | null; // 1-sigma variability ≈ 81/sqrt(K)
  eigenvalues: number[]; // orientation tensor, descending
}

// [STA-02] [STA-03] [STA-04] [STA-05]
/**
 * Axial Fisher statistics. Vectors are aligned to the principal eigenvector
 * before summation, so data crossing the primitive circle is handled.
 * Weighted: N and R use weights (e.g. Terzaghi), as in DIPS.
 */
export function fisherStats(vs: Vec3[], opts: { weights?: number[]; level?: number } = {}): FisherStats {
  const level = opts.level ?? 0.95;
  const n = vs.length;
  if (n === 0) throw new Error("no data");
  const w = opts.weights ?? vs.map(() => 1);
  const { values, vectors } = eigSym3(orientationTensor(vs, w));
  const axis = vectors[0];
  let sx = 0, sy = 0, sz = 0, W = 0;
  vs.forEach((v0, k) => {
    let v = unit(v0);
    if (dot(v, axis) < 0) v = [-v[0], -v[1], -v[2]];
    sx += w[k] * v[0]; sy += w[k] * v[1]; sz += w[k] * v[2]; W += w[k];
  });
  const R = Math.hypot(sx, sy, sz);
  let mean = unit([sx, sy, sz]);
  if (mean[2] > 0) mean = [-mean[0], -mean[1], -mean[2]];
  let K: number | null = null, conf: number | null = null, var1: number | null = null;
  if (n >= 2 && W > R) {
    K = (W - 1) / (W - R);
    const p = 1 - level;
    const arg = 1 - ((W - R) / R) * (Math.pow(1 / p, 1 / (W - 1)) - 1);
    conf = Math.acos(Math.max(-1, Math.min(1, arg))) / DEG;
    var1 = 81 / Math.sqrt(K);
  } else if (n >= 2) { K = Infinity; conf = 0; var1 = 0; }
  return { n, weightSum: W, mean, R, K, confidenceDeg: conf, level, variability1Deg: var1, eigenvalues: values };
}

// [STA-06]
/**
 * DIPS "Global Mean": plain vector sum of the lower-hemisphere poles, with
 * NO axial alignment (Diederichs 1990, sec. 3.4.2). Matches DIPS exactly, but
 * a set of subvertical planes dipping both ways averages to a near-horizontal
 * plane, so it is only meaningful for one moderately dipping cluster.
 */
export function globalMean(vs: Vec3[], w?: number[]): Vec3 {
  let sx = 0, sy = 0, sz = 0;
  vs.forEach((v0, k) => {
    let v = unit(v0); if (v[2] > 0) v = [-v[0], -v[1], -v[2]];
    const wk = w ? w[k] : 1; sx += wk * v[0]; sy += wk * v[1]; sz += wk * v[2];
  });
  let m = unit([sx, sy, sz]); if (m[2] > 0) m = [-m[0], -m[1], -m[2]];
  return m;
}

// [STA-07]
/** DIPS "Global Best Fit": pole of the best-fit great circle through all poles
 *  = eigenvector of the smallest eigenvalue of the orientation tensor
 *  (fold axis for a girdle). */
export function globalBestFit(vs: Vec3[], w?: number[]): Vec3 {
  let v = eigSym3(orientationTensor(vs, w)).vectors[2];
  if (v[2] > 0) v = [-v[0], -v[1], -v[2]];
  return v;
}
